import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Tag, Space, message, Modal, Form, Input, Switch,
  Popconfirm, Tooltip, Badge, Select, InputNumber, Divider, Alert, Image
} from 'antd';
import {
  EditOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined,
  GlobalOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined,
  SettingOutlined, VideoCameraOutlined, UserSwitchOutlined, PlayCircleOutlined, EyeOutlined,
  LinkOutlined, CopyOutlined
} from '@ant-design/icons';
import { apiClient } from '@/services/api';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;
const { Search } = Input;

interface Character {
  id: string;
  name: string;
  videoUrl: string;
  timestamps: string;
  sourceType: string; // UPLOAD 或 FROM_VIDEO
  isGlobal: boolean;
  useCost: number;
  createdAt: string;
  characterId?: string; // Sora2 返回的 character_id
  user?: {
    id: string;
    username: string;
    email: string;
  };
}

interface CharacterManagementProps {
  onConfigChange?: () => void;
}

const CharacterManagement: React.FC<CharacterManagementProps> = ({ onConfigChange }) => {
  const [loading, setLoading] = useState(false);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    isGlobal: undefined as boolean | undefined,
    userId: undefined as string | undefined
  });
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [configForm] = Form.useForm();
  const [characterConfig, setCharacterConfig] = useState({
    uploadCost: 10,
    createCost: 10
  });
  const [previewVideo, setPreviewVideo] = useState<{ visible: boolean; url: string; name: string }>({
    visible: false,
    url: '',
    name: ''
  });
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState<Character | null>(null);
  const [editForm] = Form.useForm();
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [viewingLink, setViewingLink] = useState<{ url: string; name: string }>({ url: '', name: '' });

  // 加载角色列表
  const loadCharacters = async (page = 1, pageSize = 20) => {
    try {
      setLoading(true);
      const params: any = {
        page,
        pageSize
      };
      if (filters.isGlobal !== undefined) {
        params.isGlobal = filters.isGlobal;
      }
      if (filters.userId) {
        params.userId = filters.userId;
      }

      const res = await apiClient.get('/video/admin/characters', { params });
      if (res.success && res.data) {
        setCharacters(res.data.characters || []);
        setPagination({
          current: res.data.pagination?.page || page,
          pageSize: res.data.pagination?.pageSize || pageSize,
          total: res.data.pagination?.total || 0
        });
      }
    } catch (error: any) {
      console.error('加载角色列表失败:', error);
      message.error('加载角色列表失败: ' + (error?.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 加载配置
  const loadConfig = async () => {
    try {
      // 分别获取两个配置项
      const [uploadRes, createRes] = await Promise.all([
        apiClient.get('/admin/config/character_upload_cost'),
        apiClient.get('/admin/config/character_create_cost')
      ]);
      
      const uploadCost = uploadRes.success && uploadRes.data?.value 
        ? parseInt(uploadRes.data.value) 
        : 10;
      const createCost = createRes.success && createRes.data?.value 
        ? parseInt(createRes.data.value) 
        : 10;
      
      setCharacterConfig({
        uploadCost,
        createCost
      });
      configForm.setFieldsValue({
        uploadCost,
        createCost
      });
    } catch (error) {
      console.error('加载配置失败:', error);
      // 使用默认值
      setCharacterConfig({
        uploadCost: 10,
        createCost: 10
      });
      configForm.setFieldsValue({
        uploadCost: 10,
        createCost: 10
      });
    }
  };

  useEffect(() => {
    loadCharacters();
    loadConfig();
  }, [filters]);

  // 设置/取消公共角色
  const handleToggleGlobal = async (id: string, isGlobal: boolean) => {
    try {
      const res = await apiClient.put(`/video/admin/characters/${id}`, { isGlobal });
      if (res.success) {
        message.success(isGlobal ? '已设置为公共角色' : '已取消公共角色');
        loadCharacters(pagination.current, pagination.pageSize);
      } else {
        message.error(res.message || '操作失败');
      }
    } catch (error: any) {
      console.error('设置公共角色失败:', error);
      message.error('操作失败: ' + (error?.response?.data?.message || error.message));
    }
  };

  // 更新角色使用积分
  const handleUpdateUseCost = async (id: string, useCost: number) => {
    try {
      const res = await apiClient.put(`/video/admin/characters/${id}`, { useCost });
      if (res.success) {
        message.success(`已更新角色使用积分为 ${useCost}`);
        loadCharacters(pagination.current, pagination.pageSize);
      } else {
        message.error(res.message || '操作失败');
      }
    } catch (error: any) {
      console.error('更新角色使用积分失败:', error);
      message.error('操作失败: ' + (error?.response?.data?.message || error.message));
    }
  };

  // 打开编辑模态框
  const handleOpenEdit = (record: Character) => {
    setEditingCharacter(record);
    editForm.setFieldsValue({
      videoUrl: record.videoUrl,
      timestamps: record.timestamps
    });
    setEditModalVisible(true);
  };

  // 保存编辑
  const handleSaveEdit = async () => {
    if (!editingCharacter) return;

    try {
      const values = await editForm.validateFields();
      const res = await apiClient.put(`/video/admin/characters/${editingCharacter.id}`, {
        videoUrl: values.videoUrl,
        timestamps: values.timestamps
      });

      if (res.success) {
        message.success('角色信息已更新');
        setEditModalVisible(false);
        setEditingCharacter(null);
        loadCharacters(pagination.current, pagination.pageSize);
      } else {
        message.error(res.message || '更新失败');
      }
    } catch (error: any) {
      if (error?.errorFields) {
        return; // 表单验证错误
      }
      console.error('更新角色失败:', error);
      message.error('更新失败: ' + (error?.response?.data?.message || error.message));
    }
  };

  // 查看视频链接
  const handleViewLink = (record: Character) => {
    setViewingLink({
      url: record.videoUrl || '',
      name: record.name
    });
    setLinkModalVisible(true);
  };

  // 复制链接
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(viewingLink.url);
      message.success('链接已复制到剪贴板');
    } catch (error) {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.value = viewingLink.url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success('链接已复制到剪贴板');
      } catch (err) {
        message.error('复制失败，请手动复制');
      }
      document.body.removeChild(textArea);
    }
  };

  // 删除角色
  const handleDelete = async (id: string) => {
    try {
      const res = await apiClient.delete(`/video/admin/characters/${id}`);
      if (res.success) {
        message.success('角色已删除');
        loadCharacters(pagination.current, pagination.pageSize);
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (error: any) {
      console.error('删除角色失败:', error);
      message.error('删除失败: ' + (error?.response?.data?.message || error.message));
    }
  };

  // 保存配置
  const handleSaveConfig = async () => {
    try {
      const values = await configForm.validateFields();
      
      // 分别保存两个配置项
      await Promise.all([
        apiClient.post('/admin/config', {
          key: 'character_upload_cost',
          value: String(values.uploadCost)
        }),
        apiClient.post('/admin/config', {
          key: 'character_create_cost',
          value: String(values.createCost)
        })
      ]);

      message.success('配置已保存');
      setConfigModalVisible(false);
      setCharacterConfig({
        uploadCost: values.uploadCost,
        createCost: values.createCost
      });
      if (onConfigChange) {
        onConfigChange();
      }
    } catch (error: any) {
      if (error?.errorFields) {
        return; // 表单验证错误
      }
      console.error('保存配置失败:', error);
      message.error('保存配置失败: ' + (error?.response?.data?.message || error.message));
    }
  };

  // 预览角色视频
  const handlePreviewVideo = (record: Character) => {
    if (!record.videoUrl) {
      message.warning('该角色没有视频');
      return;
    }

    // 处理不同类型的视频 URL
    let videoUrl = record.videoUrl;
    
    // 如果是 base64 格式（纯 base64 字符串，不包含 data: 前缀）
    if (!videoUrl.startsWith('http://') && 
        !videoUrl.startsWith('https://') && 
        !videoUrl.startsWith('data:')) {
      // 检查是否为 base64 格式
      const base64Pattern = /^[A-Za-z0-9+/=]+$/;
      if (base64Pattern.test(videoUrl.replace(/\s/g, ''))) {
        videoUrl = `data:video/mp4;base64,${videoUrl}`;
      }
    }

    setPreviewVideo({
      visible: true,
      url: videoUrl,
      name: record.name
    });
  };

  const columns: ColumnsType<Character> = [
    {
      title: '角色预览',
      key: 'preview',
      width: 150,
      fixed: 'left' as const,
      render: (_: any, record: Character) => {
        // 判断是否为有效的视频 URL
        const isVideoUrl = record.videoUrl && (
          record.videoUrl.startsWith('http://') || 
          record.videoUrl.startsWith('https://') ||
          record.videoUrl.startsWith('data:video/') ||
          /^[A-Za-z0-9+/=]+$/.test(record.videoUrl.replace(/\s/g, '')) // base64 格式
        );
        
        // 如果是 base64，转换为 data URL
        const videoSrc = record.videoUrl && !record.videoUrl.startsWith('http') && !record.videoUrl.startsWith('data:')
          ? `data:video/mp4;base64,${record.videoUrl}`
          : record.videoUrl;
        
        const isFailed = !record.characterId; // 没有 characterId，视为创建失败

        return (
          <div className="relative group">
            {/* 失败标识 */}
            {isFailed && (
              <div className="absolute top-1 left-1 z-10 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded shadow-sm flex items-center gap-1">
                <span>❌</span>
                <span>创建失败</span>
              </div>
            )}
            {isVideoUrl ? (
              <div 
                className="w-28 h-20 rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:shadow-lg transition-all relative"
                onClick={() => handlePreviewVideo(record)}
              >
                <video
                  src={videoSrc}
                  className="w-full h-full object-cover"
                  preload="metadata"
                  muted
                  playsInline
                  onMouseEnter={(e) => {
                    const video = e.currentTarget;
                    try {
                      video.play().catch(() => {});
                    } catch (err) {
                      // 忽略播放错误
                    }
                  }}
                  onMouseLeave={(e) => {
                    const video = e.currentTarget;
                    try {
                      video.pause();
                      video.currentTime = 0;
                    } catch (err) {
                      // 忽略暂停错误
                    }
                  }}
                  onError={() => {
                    // 视频加载失败时显示占位符
                    console.warn('视频加载失败:', record.videoUrl);
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all">
                  <PlayCircleOutlined className="text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="absolute bottom-1 right-1 bg-black bg-opacity-60 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                  <EyeOutlined /> 预览
                </div>
              </div>
            ) : (
              <div className="w-28 h-20 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex flex-col items-center justify-center border border-gray-200">
                <VideoCameraOutlined className="text-2xl text-blue-400 mb-1" />
                <span className="text-xs text-gray-500">无视频</span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: '角色名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record: Character) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{text}</span>
          {record.isGlobal && (
            <Tag color="green" icon={<GlobalOutlined />}>
              公共
            </Tag>
          )}
        </div>
      )
    },
    {
      title: '创建者',
      dataIndex: 'user',
      key: 'user',
      width: 200,
      render: (user: any) => (
        user ? (
          <div>
            <div className="font-medium">{user.username || user.email}</div>
            <div className="text-xs text-gray-500">{user.email}</div>
          </div>
        ) : (
          <span className="text-gray-400">系统</span>
        )
      )
    },
    {
      title: '来源',
      dataIndex: 'sourceType',
      key: 'sourceType',
      width: 120,
      render: (type: string) => (
        <Tag color={type === 'UPLOAD' ? 'blue' : 'purple'}>
          {type === 'UPLOAD' ? '上传视频' : '来源视频'}
        </Tag>
      )
    },
    {
      title: '时间范围',
      dataIndex: 'timestamps',
      key: 'timestamps',
      width: 120,
      render: (timestamps: string) => (
        <span className="text-sm text-gray-600">{timestamps}秒</span>
      )
    },
    {
      title: '使用积分',
      dataIndex: 'useCost',
      key: 'useCost',
      width: 150,
      render: (useCost: number, record: Character) => (
        <Space.Compact style={{ width: 150 }}>
          <InputNumber
            min={0}
            max={1000}
            value={useCost || 0}
            onChange={(value) => {
              if (value !== null && value !== undefined) {
                handleUpdateUseCost(record.id, value);
              }
            }}
            size="small"
            style={{ width: '100%' }}
          />
          <Button disabled size="small" style={{ width: '50px' }}>积分</Button>
        </Space.Compact>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => (
        <span className="text-sm text-gray-600">
          {new Date(date).toLocaleString('zh-CN')}
        </span>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      fixed: 'right' as const,
      render: (_: any, record: Character) => (
        <Space size="small" wrap>
          <Tooltip title="编辑视频链接">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenEdit(record)}
            >
              编辑
            </Button>
          </Tooltip>
          <Tooltip title="查看视频链接">
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => handleViewLink(record)}
            >
              链接
            </Button>
          </Tooltip>
          <Tooltip title={record.isGlobal ? '取消公共角色' : '设为公共角色'}>
            <Button
              type="link"
              size="small"
              icon={record.isGlobal ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleToggleGlobal(record.id, !record.isGlobal)}
            >
              {record.isGlobal ? '取消公共' : '设为公共'}
            </Button>
          </Tooltip>
          <Popconfirm
            title="确定要删除这个角色吗？"
            description="删除后无法恢复，所有使用该角色的视频生成将受影响。"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <UserSwitchOutlined />
              角色管理
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              管理所有用户上传的角色，可以设置为公共角色供所有用户使用，展示角色缩略图或角色视频。使用积分默认为20积分。
            </p>
          </div>
          <Space>
            <Button
              icon={<SettingOutlined />}
              onClick={() => {
                setConfigModalVisible(true);
                loadConfig();
              }}
            >
              角色配置
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadCharacters(pagination.current, pagination.pageSize)}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        </div>

        {/* 筛选器 */}
        <div className="mb-4 flex gap-2">
          <Select
            placeholder="筛选类型"
            style={{ width: 150 }}
            allowClear
            value={filters.isGlobal}
            onChange={(value) => setFilters({ ...filters, isGlobal: value })}
          >
            <Option value={true}>公共角色</Option>
            <Option value={false}>个人角色</Option>
          </Select>
          <Button onClick={() => setFilters({ isGlobal: undefined, userId: undefined })}>
            清除筛选
          </Button>
        </div>

        {/* 统计信息 */}
        <div className="mb-4 grid grid-cols-3 gap-4">
          <Card size="small" className="text-center">
            <div className="text-2xl font-bold text-blue-600">{pagination.total}</div>
            <div className="text-sm text-gray-600">总角色数</div>
          </Card>
          <Card size="small" className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {characters.filter(c => c.isGlobal).length}
            </div>
            <div className="text-sm text-gray-600">公共角色</div>
          </Card>
          <Card size="small" className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {characters.filter(c => !c.isGlobal).length}
            </div>
            <div className="text-sm text-gray-600">个人角色</div>
          </Card>
        </div>

        {/* 角色列表 */}
        <Table
          columns={columns}
          dataSource={characters}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个角色`,
            onChange: (page, pageSize) => {
              loadCharacters(page, pageSize);
            }
          }}
          scroll={{ x: 1600 }}
        />
      </Card>

      {/* 配置模态框 */}
      <Modal
        title="角色配置"
        open={configModalVisible}
        onOk={handleSaveConfig}
        onCancel={() => setConfigModalVisible(false)}
        okText="保存"
        cancelText="取消"
      >
        <Form form={configForm} layout="vertical">
          <Alert
            message="角色积分消耗配置"
            description="设置用户上传或创建角色时需要消耗的积分数"
            type="info"
            showIcon
            className="mb-4"
          />
          <Form.Item
            label="上传角色消耗积分"
            name="uploadCost"
            rules={[{ required: true, message: '请输入积分数量' }]}
            tooltip="用户上传角色视频时消耗的积分数"
          >
            <Space.Compact style={{ width: '100%' }}>
              <InputNumber
                min={0}
                max={1000}
                style={{ width: '100%' }}
              />
              <Button disabled style={{ width: '60px' }}>积分</Button>
            </Space.Compact>
          </Form.Item>
          <Form.Item
            label="创建角色消耗积分"
            name="createCost"
            rules={[{ required: true, message: '请输入积分数量' }]}
            tooltip="用户从视频创建角色时消耗的积分数"
          >
            <Space.Compact style={{ width: '100%' }}>
              <InputNumber
                min={0}
                max={1000}
                style={{ width: '100%' }}
              />
              <Button disabled style={{ width: '60px' }}>积分</Button>
            </Space.Compact>
          </Form.Item>
        </Form>
      </Modal>

      {/* 视频预览模态框 */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <VideoCameraOutlined />
            <span>预览角色视频 - {previewVideo.name}</span>
          </div>
        }
        open={previewVideo.visible}
        onCancel={() => setPreviewVideo({ visible: false, url: '', name: '' })}
        footer={[
          <Button key="close" onClick={() => setPreviewVideo({ visible: false, url: '', name: '' })}>
            关闭
          </Button>
        ]}
        width={900}
        centered
        destroyOnHidden
      >
        <div className="w-full">
          {previewVideo.url && (
            <div className="relative w-full bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <video
                src={previewVideo.url.startsWith('http') || previewVideo.url.startsWith('data:')
                  ? previewVideo.url
                  : `data:video/mp4;base64,${previewVideo.url}`}
                controls
                className="w-full h-full"
                autoPlay
                style={{ maxHeight: '70vh' }}
                onError={(e) => {
                  console.error('视频播放失败:', e);
                  message.error('视频加载失败，请检查视频 URL 是否有效');
                }}
              />
            </div>
          )}
        </div>
      </Modal>

      {/* 编辑角色模态框 */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <EditOutlined />
            <span>编辑角色 - {editingCharacter?.name}</span>
          </div>
        }
        open={editModalVisible}
        onOk={handleSaveEdit}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingCharacter(null);
          editForm.resetFields();
        }}
        okText="保存"
        cancelText="取消"
        width={700}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical" className="mt-4">
          <Form.Item
            label="视频链接 (videoUrl)"
            name="videoUrl"
            rules={[
              { required: true, message: '请输入视频链接' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  // 验证 URL 格式或 base64 格式
                  const isUrl = value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:');
                  const isBase64 = /^[A-Za-z0-9+/=]+$/.test(value.replace(/\s/g, ''));
                  // 🔥 也允许 PID 格式（s_开头的字符串），用于 FROM_VIDEO 类型的角色
                  const isPid = /^s_[a-zA-Z0-9]+$/.test(value);
                  if (isUrl || isBase64 || isPid) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('请输入有效的视频 URL、base64 编码的视频数据或 PID（s_开头）'));
                }
              }
            ]}
            tooltip={
              editingCharacter?.sourceType === 'FROM_VIDEO' 
                ? '对于从视频创建的角色，这里存储的是原始视频的 URL。如果是 PID（s_开头），表示这是视频的标识符，用于 API 调用。'
                : '支持 HTTP/HTTPS URL、data URL 或 base64 编码的视频数据。对于上传的角色，这里存储的是上传的视频 URL。'
            }
          >
            <Input.TextArea
              rows={4}
              placeholder={
                editingCharacter?.sourceType === 'FROM_VIDEO'
                  ? '请输入视频 URL 或 PID（例如：https://example.com/video.mp4 或 s_xxxxxxxxxxxxxxx）'
                  : '请输入视频链接，例如：https://example.com/video.mp4 或 data:video/mp4;base64,...'
              }
              showCount
              maxLength={5000}
            />
          </Form.Item>
          <Form.Item
            label="时间范围 (timestamps)"
            name="timestamps"
            rules={[
              { required: true, message: '请输入时间范围' },
              {
                pattern: /^\d+,\d+$/,
                message: '格式错误，应为 "开始秒数,结束秒数"，例如: "0,3"'
              }
            ]}
            tooltip="视频使用的时间范围，格式：开始秒数,结束秒数，例如：0,3 表示使用视频的前3秒"
          >
            <Input placeholder="例如：0,3" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看视频链接模态框 */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <LinkOutlined />
            <span>查看视频链接 - {viewingLink.name}</span>
          </div>
        }
        open={linkModalVisible}
        onCancel={() => {
          setLinkModalVisible(false);
          setViewingLink({ url: '', name: '' });
        }}
        footer={[
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={handleCopyLink}
          >
            复制链接
          </Button>,
          <Button key="close" onClick={() => {
            setLinkModalVisible(false);
            setViewingLink({ url: '', name: '' });
          }}>
            关闭
          </Button>
        ]}
        width={800}
        destroyOnHidden
      >
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-600 mb-2">视频链接 (videoUrl):</div>
            <Input.TextArea
              value={viewingLink.url}
              readOnly
              rows={6}
              className="font-mono text-sm"
              style={{ cursor: 'text' }}
              onClick={(e) => {
                (e.target as HTMLTextAreaElement).select();
              }}
            />
          </div>
          <Alert
            message="提示"
            description='点击文本框可全选链接，或点击"复制链接"按钮复制到剪贴板'
            type="info"
            showIcon
          />
        </div>
      </Modal>
    </div>
  );
};

export default CharacterManagement;

